const BROADCAST_CHANNEL_NAME = 'common-channel';

/**
 * The type of messages that can be broadcasted.
 */
export enum MessageType {
  LOGIN = 'LOGIN',
  LOGOUT = 'LOGOUT',
}

type Message = {
  type: MessageType;
};

type PostOptions = {
  skipLocalSubscribers?: boolean;
};

/**
 * A service for broadcasting messages to all tabs/windows with the same origin.
 */
class BroadcastService {
  #channel: BroadcastChannel;

  #subscribers: Array<(message: Message) => void>;

  #handleMessageEvent: (event: MessageEvent) => void;

  // Export through a class instance.
  readonly MessageType = MessageType;

  constructor() {
    this.#channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
    this.#subscribers = [];
    this.#handleMessageEvent = (event: MessageEvent) => {
      this.#subscribers.forEach((callback) => callback(event.data));
    };
    this.#channel.addEventListener('message', this.#handleMessageEvent);
  }

  #postMessage(options: PostOptions, message: Message) {
    this.#channel.postMessage(message);
    if (!options.skipLocalSubscribers) {
      this.#subscribers.forEach((callback) => callback(message));
    }
  }

  postLogin(options: PostOptions = {}) {
    this.#postMessage(options, { type: MessageType.LOGIN });
  }

  postLogout(options: PostOptions = {}) {
    this.#postMessage(options, { type: MessageType.LOGOUT });
  }

  subscribe(callback: (message: Message) => void): () => void {
    this.#subscribers.push(callback);
    return () => {
      this.#subscribers = this.#subscribers.filter((cb) => cb !== callback);
    };
  }

  close() {
    this.#channel.removeEventListener('message', this.#handleMessageEvent);
    this.#channel.close();
    this.#subscribers.length = 0;
  }
}

export const broadcastService = new BroadcastService();
